import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, SafeAreaView, TextInput, TouchableOpacity, ScrollView, KeyboardAvoidingView, Platform, Alert, Image, ActivityIndicator } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import { useRouter } from 'expo-router';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { storageService } from '../services/storageService';
import AccionesDeChat, { AvisoDeBloqueo } from '../components/AccionesDeChat';

interface ChatMessage {
  id: string;
  text: string;
  isMine: boolean;
  time: string;
  isSystemLink?: boolean;
  /** Comprobante de transferencia. Ver `enviarComprobante`. */
  imagenUrl?: string | null;
}

/** Convierte texto en un slug estable para usar como parte del chatId. */
function slugify(s: string): string {
  return String(s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'anon';
}

export default function RentCarChatScreen() {
  const router = useRouter();
  const userRole = useAppStore(state => state.userRole);
  const activeChatPartner = useAppStore(state => state.activeChatPartner);
  const ownerUid = useAppStore(state => state.userFirebaseUid);

  const partnerName = activeChatPartner ? activeChatPartner.name : 'Candidato';
  const partnerVehicle = activeChatPartner ? activeChatPartner.vehicle : 'Vehículo de interés';
  const partnerRole = activeChatPartner ? activeChatPartner.role : 'CANDIDATE';

  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const [inputText, setInputText] = useState('');
  const [showMagicLink, setShowMagicLink] = useState(false);

  const myUid = ownerUid ?? '';

  // FIX AUDIT — chatId determinístico basado en los DOS uids (dueño + renter),
  // para que AMBAS partes escriban/lean el MISMO documento. Antes se armaba con
  // slug(nombre), distinto en cada lado → nunca coincidían. El dueño lo abre
  // desde su bandeja (partnerUid = uid del renter); el renter desde el
  // marketplace (ownerId = car.ownerId). El chatId empieza con `${ownerId}_`
  // para que la bandeja del dueño (listenToOwnerNegotiations, prefix match) lo
  // encuentre. La autorización de mensajes es por senderId/recipientId.
  const isOwnerViewer = userRole === 'RENT_CAR_OWNER';
  const ownerId = isOwnerViewer ? myUid : (activeChatPartner?.ownerId ?? '');
  const renterId = isOwnerViewer ? (activeChatPartner?.partnerUid ?? '') : myUid;
  const otherPartyUid = isOwnerViewer ? renterId : ownerId;
  const [chatBloqueado, setChatBloqueado] = useState(false);
  // Si el otro me bloqueó a mí, la caja se cierra igual: escribir para que el
  // servidor lo deniegue se lee como una app rota.
  useEffect(() => {
    let vivo = true;
    if (!myUid || !otherPartyUid) return;
    firestoreService
      .hayBloqueoEntre(String(myUid), String(otherPartyUid))
      .then((r) => { if (vivo) setChatBloqueado(!!r); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [myUid, otherPartyUid]);
  const onboardOwnerId = ownerId;
  const chatId = useMemo(() => {
    if (!ownerId || !renterId) return '';
    return `${ownerId}__${renterId}`;
  }, [ownerId, renterId]);

  // Listener del chat — sin esto los mensajes que el owner enviaba iban a
  // Firestore pero nunca reaparecían en la UI (ni la del owner ni la del
  // candidato). El estado local se quedaba con lo tipeado y punto.
  useEffect(() => {
    // V07 — el servicio filtra por el uid propio (la regla lee por
    // senderId/recipientId), así que sin sesión no hay nada que escuchar.
    if (!chatId || !myUid) return;
    const unsub = firestoreService.listenToRentNegotiationMessages(chatId, myUid, (msgs) => {
      const mapped: ChatMessage[] = msgs.map((m: any) => ({
        id: m.id,
        text: String(m.text ?? ''),
        isMine: m.senderId === myUid,
        time: m.timestamp?.toDate?.()?.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' }) ?? 'Ahora',
        isSystemLink: !!m.isSystemLink,
        // Sin esto el comprobante se guardaba, viajaba... y la burbuja salia
        // con su texto y sin la foto.
        imagenUrl: typeof m.imagenUrl === 'string' ? m.imagenUrl : null,
      }));
      setMessages(mapped);
    });
    return () => { try { unsub?.(); } catch {} };
  }, [chatId, myUid]);

  /**
   * Deja constancia EN PANTALLA de algo que no se pudo enviar.
   *
   * POR QUÉ: antes, cuando faltaba el chatId o el destinatario, `sendMessage`
   * hacía `return` a secas — el mensaje desaparecía sin aviso, sin error y sin
   * la marca "(no enviado)" que sí existía para el fallo de red. Ese silencio
   * es el "Unresponsive UI element" que ya costó un rechazo. Se usa una burbuja
   * local (no Alert) porque se ve en TODAS las plataformas, incluida la web.
   */
  const avisoLocal = (texto: string) => {
    setMessages((prev) => [...prev, {
      // Sufijo aleatorio: dos toques en el mismo milisegundo compartirían la
      // key de React y una de las burbujas no se pintaría.
      id: 'aviso-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
      text: texto,
      isMine: true,
      time: 'Ahora',
    }]);
  };

  /** Explica por qué esta conversación no puede enviar nada. Sin inventar. */
  const motivoChatNoDisponible = (): string => {
    if (!myUid) return '⚠️ No se envió: no hay sesión iniciada. Vuelve a entrar a tu cuenta e inténtalo de nuevo.';
    if (!otherPartyUid || !chatId) {
      return isOwnerViewer
        ? '⚠️ No se envió: esta conversación no está enlazada con la cuenta del chofer, así que el mensaje no llegaría a nadie. Ábrela desde "Chats" o pídele que te escriba desde el Marketplace.'
        : '⚠️ No se envió: no pudimos identificar al dueño de este vehículo. Vuelve al Marketplace y toca "Contactar" de nuevo.';
    }
    return '⚠️ No se envió. Inténtalo de nuevo.';
  };

  const puedeEnviar = !!chatId && !!otherPartyUid && !!myUid;

  const sendMessage = async () => {
    const pendiente = inputText.trim();
    if (!pendiente) return;
    if (!puedeEnviar) {
      // No se limpia el input: lo escrito no se pierde y se puede reintentar.
      avisoLocal(motivoChatNoDisponible());
      return;
    }
    const text = pendiente;
    setInputText('');

    // Persistir el mensaje en Firestore con senderId + recipientId (las reglas
    // autorizan lectura/escritura por esos campos, no por el chatId).
    try {
      await firestoreService.sendRentCarMessage(chatId, {
        text,
        senderId: myUid,
        recipientId: otherPartyUid,
        senderRole: userRole,
        partnerName,
        partnerVehicle,
      });
    } catch (e) {
      console.warn('No se pudo persistir mensaje del chat:', e);
      // Mensaje falló → mostrar en local para que el owner sepa que quedó.
      setMessages((prev) => [...prev, {
        id: 'local-' + Date.now(),
        text: text + ' ⚠️ (no enviado)',
        isMine: true,
        time: 'Ahora',
      }]);
    }
  };

  // ══════════════════════════════════════════════════════════════════════
  // EL COMPROBANTE DE LA TRANSFERENCIA, DENTRO DEL MISMO CHAT
  // ══════════════════════════════════════════════════════════════════════
  //
  // Lo pidió el fundador (3-oct-2026): «un medio de conexión donde el usuario
  // pueda subir su comprobante que le transfirió al dueño».
  //
  // Va AQUÍ y no en una pantalla aparte porque el dinero del alquiler no pasa
  // por Go Safely: lo acuerdan y lo pagan ellos dos. Lo único que falta es que
  // la foto llegue, quede guardada y los dos la puedan ver. El chat ya es el
  // sitio donde se están hablando; una pantalla nueva sería un sitio más que
  // alguien tiene que encontrar.
  //
  // ⚠️ Go Safely NO valida ese pago ni lo acredita en ninguna parte. La foto es
  // evidencia entre ellos, y el texto que la acompaña lo dice.
  const [subiendoComprobante, setSubiendoComprobante] = useState(false);

  const enviarComprobante = async () => {
    if (!puedeEnviar) {
      avisoLocal(motivoChatNoDisponible());
      return;
    }
    if (!ownerId) {
      avisoLocal('⚠️ No pudimos identificar al dueño del vehículo. Vuelve a abrir el chat desde el Marketplace.');
      return;
    }
    if (subiendoComprobante) return;

    // ⚠️ NO se pide permiso de galería a propósito. `launchImageLibraryAsync`
    // no lo necesita en ninguna plataforma, y pedirlo antes ROMPE la web:
    // el diálogo de permiso consume el gesto del usuario y el navegador ya no
    // abre el selector de archivos. Mismo criterio que el carrito.
    let elegida;
    try {
      elegida = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 0.8,
      });
    } catch (e: any) {
      avisoLocal('⚠️ No pudimos abrir tus fotos. Inténtalo de nuevo.');
      return;
    }
    if (elegida.canceled || !elegida.assets?.[0]?.uri) return;

    setSubiendoComprobante(true);
    try {
      // Una foto de 12 MP a calidad 0.8 pasa del tope de 10 MB de la regla de
      // Storage y la subida se rechazaría SIN decir por qué. Mismo encogido
      // que el comprobante del carrito: 1400 px de ancho, JPEG al 70%, sin
      // recortar — el monto y la referencia tienen que seguir leyéndose.
      let uri = elegida.assets[0].uri;
      try {
        const reducida = await ImageManipulator.manipulateAsync(
          uri,
          [{ resize: { width: 1400 } }],
          { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG }
        );
        uri = reducida.uri;
      } catch (e: any) {
        console.warn('[rent-chat] no se pudo redimensionar el comprobante:', e?.message ?? e);
      }

      const url = await storageService.uploadRentReceipt(ownerId, myUid, uri);
      await firestoreService.sendRentCarMessage(chatId, {
        // Las reglas exigen `text` no vacío, así que el comprobante viaja con
        // su propia frase — que además es la que explica qué es.
        text: '📄 Te envié el comprobante de la transferencia.',
        imagenUrl: url,
        senderId: myUid,
        recipientId: otherPartyUid,
        senderRole: userRole,
        partnerName,
        partnerVehicle,
      });
    } catch (e: any) {
      console.warn('[rent-chat] no se pudo enviar el comprobante:', e?.message ?? e);
      avisoLocal('⚠️ No se envió el comprobante. Revisa tu conexión e inténtalo otra vez.');
    } finally {
      setSubiendoComprobante(false);
    }
  };

  // Botón "+" del chat. Antes NO tenía onPress → Google Play rechazó por
  // "Unresponsive UI elements". Muestra respuestas rápidas (templates útiles
  // en negociación de rentas) y las envía al chat existente. No modifica el
  // diseño ni añade dependencias.
  const sendQuickReply = async (text: string) => {
    if (!puedeEnviar) {
      // Antes solo salía un Alert (invisible en web) y nada más. Ahora queda
      // dicho dentro del propio chat, en cualquier plataforma.
      Alert.alert('Chat no disponible', 'La conversación aún no está lista.');
      avisoLocal(motivoChatNoDisponible());
      return;
    }
    try {
      await firestoreService.sendRentCarMessage(chatId, {
        text,
        senderId: myUid,
        recipientId: otherPartyUid,
        senderRole: userRole,
        partnerName,
        partnerVehicle,
      });
    } catch (e) {
      console.warn('No se pudo enviar la respuesta rápida:', e);
      // El fallo de red tampoco se traga en silencio: se marca "(no enviado)"
      // igual que un mensaje escrito a mano.
      setMessages((prev) => [...prev, {
        id: 'quick-local-' + Date.now(),
        text: text + ' ⚠️ (no enviado — verifica tu conexión)',
        isMine: true,
        time: 'Ahora',
      }]);
    }
  };

  const handleQuickReplyMenu = () => {
    Alert.alert(
      'Respuestas rápidas',
      'Toca una para enviarla al chat.',
      [
        { text: '¿Está disponible?', onPress: () => sendQuickReply('¿Está disponible este vehículo?') },
        { text: '¿Puedo verlo hoy?', onPress: () => sendQuickReply('¿Puedo verlo hoy en persona?') },
        { text: '¿Cuál es el precio final?', onPress: () => sendQuickReply('¿Cuál es el precio final semanal?') },
        { text: 'Cancelar', style: 'cancel' },
      ]
    );
  };

  const generateLink = async () => {
    // FIX BLOQUEADOR (auditoría) — Antes el enlace solo se agregaba al estado
    // LOCAL del dueño (setMessages), así que NUNCA se escribía en Firestore y
    // el CANDIDATO (otro dispositivo) jamás lo recibía → el onboarding quedaba
    // muerto. Ahora lo PERSISTIMOS vía sendRentCarMessage (isSystemLink:true) y
    // reaparece por el listener en ambas pantallas. Dominio corregido al host
    // real (gosafely-3e0e7.web.app); en nativo el botón usa router.push.
    const linkText = `¡Excelente! Hemos llegado a un acuerdo. Por favor haz clic en el siguiente enlace seguro para subir tus documentos y registrarte en mi CRM: \n\n🔗 https://gosafely-3e0e7.web.app/onboard/${onboardOwnerId}`;
    setShowMagicLink(true);

    if (chatId && otherPartyUid) {
      try {
        await firestoreService.sendRentCarMessage(chatId, {
          text: linkText,
          senderId: myUid,
          recipientId: otherPartyUid,
          senderRole: userRole,
          partnerName,
          partnerVehicle,
          isSystemLink: true,
        });
      } catch (e) {
        console.warn('No se pudo persistir el enlace mágico:', e);
        setMessages((prev) => [...prev, {
          id: 'magic-local-' + Date.now(),
          text: linkText + ' ⚠️ (no enviado — verifica tu conexión)',
          isMine: true,
          time: 'Ahora',
          isSystemLink: true,
        }]);
      }
    } else {
      // Sin destinatario identificado el enlace NO sale de este teléfono. Antes
      // se pintaba igual que uno enviado y el dueño quedaba esperando a un
      // candidato que nunca lo recibió: ahora se dice que no se envió.
      setMessages((prev) => [...prev, {
        id: 'magic-local-' + Date.now(),
        text: linkText,
        isMine: true,
        time: 'Ahora',
        isSystemLink: true,
      }, {
        id: 'magic-aviso-' + Date.now(),
        text: motivoChatNoDisponible() + ' Puedes copiar el enlace de arriba y hacérselo llegar por otra vía.',
        isMine: true,
        time: 'Ahora',
      }]);
    }

    // Registrar al candidato en el CRM del owner real
    if (ownerUid) {
      try {
        await firestoreService.approveCandidateToCRM(ownerUid, {
          name: partnerName,
          vehicleInterest: partnerVehicle,
          source: 'Marketplace Chat'
        });
      } catch (e) {
        console.error('Error al insertar candidato en CRM:', e);
      }
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.header}>
        <TouchableOpacity 
          onPress={() => router.canGoBack() ? router.back() : router.replace('/(tabs)/dashboard')} 
          style={[styles.backBtn, { zIndex: 10, padding: 10, flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: 20 }]}
        >
          <Ionicons name="arrow-back" size={24} color={Colors.primary} />
          <Text style={{color: Colors.primary, fontWeight: 'bold', marginLeft: 4}}>Salir</Text>
        </TouchableOpacity>
        <View style={styles.headerInfo}>
          <Text style={styles.title}>Chat: {partnerName}</Text>
          <Text style={styles.subtitle}>
            {partnerRole === 'DRIVER' ? `Chofer Activo • ${partnerVehicle}` : `Candidato • ${partnerVehicle}`}
          </Text>
        </View>
        {/* Bloquear / reportar (política de contenido de Google Play). El
            «otro» es el mismo `otherPartyUid` al que se le mandan los mensajes,
            para que bloquear y escribir hablen de la misma persona. */}
        <AccionesDeChat
          otroUid={otherPartyUid}
          otroNombre={partnerName}
          contexto="RENT_CAR"
          referenciaId={chatId}
          onCambioDeBloqueo={setChatBloqueado}
        />
        <Ionicons name="shield-checkmark" size={24} color={Colors.primary} />
      </View>

      <KeyboardAvoidingView 
        style={styles.keyboardAvoid} 
        // Mismo arreglo que chat.tsx: con `undefined` el teclado tapa la
        // caja de escribir. 'height' funciona porque el manifest tiene
        // windowSoftInputMode="adjustResize".
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView contentContainerStyle={[styles.chatContainer, messages.length === 0 && { flex: 1, justifyContent: 'center' }]}>
          <View style={styles.encryptionWarning}>
            <Ionicons name="lock-closed" size={12} color={Colors.textSecondary} />
            <Text style={styles.encryptionText}>Conexión segura (TLS). Solo tú y la otra parte ven esta conversación.</Text>
          </View>

          {messages.length === 0 ? (
            <Text style={{ color: Colors.textSecondary, textAlign: 'center', fontStyle: 'italic', marginTop: 20 }}>
              No hay mensajes en esta conversación aún. Escribe un mensaje abajo.
            </Text>
          ) : (
            messages.map((msg) => {
              if (msg.isSystemLink) {
                return (
                  <View key={msg.id} style={[styles.messageBubble, msg.isMine ? styles.myBubble : styles.theirBubble, { width: '85%', maxWidth: '85%' }]}>
                    <Text style={[styles.messageText, msg.isMine ? styles.myText : styles.theirText]}>{msg.text}</Text>
                    <TouchableOpacity 
                      style={{ 
                        backgroundColor: '#FFF', 
                        borderRadius: 12, 
                        padding: 12, 
                        marginTop: 12, 
                        alignItems: 'center',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        gap: 8
                      }} 
                      onPress={() => router.push(`/onboard/${onboardOwnerId}` as any)}
                    >
                      <Ionicons name="shield-checkmark" size={18} color={Colors.primary} />
                      <Text style={{ color: Colors.primary, fontWeight: 'bold', fontSize: 14 }}>Subir Documentos y Registro</Text>
                    </TouchableOpacity>
                    <Text style={styles.messageTime}>{msg.time}</Text>
                  </View>
                );
              }
              return (
                <View key={msg.id} style={[styles.messageBubble, msg.isMine ? styles.myBubble : styles.theirBubble]}>
                  <Text style={[styles.messageText, msg.isMine ? styles.myText : styles.theirText]}>{msg.text}</Text>
                  {!!msg.imagenUrl && (
                    <Image
                      source={{ uri: msg.imagenUrl }}
                      style={{ width: 200, height: 260, borderRadius: 10, marginTop: 8, backgroundColor: 'rgba(0,0,0,0.06)' }}
                      resizeMode="cover"
                      accessibilityLabel="Comprobante de transferencia"
                    />
                  )}
                  <Text style={styles.messageTime}>{msg.time}</Text>
                </View>
              );
            })
          )}
        </ScrollView>

        {userRole === 'RENT_CAR_OWNER' && !showMagicLink && (
          <View style={styles.actionContainer}>
            <TouchableOpacity style={styles.magicButton} onPress={generateLink}>
              <Ionicons name="link" size={20} color="#FFF" />
              <Text style={styles.magicButtonText}>Cerrar Trato (Generar Enlace CRM)</Text>
            </TouchableOpacity>
          </View>
        )}

        <AvisoDeBloqueo bloqueado={chatBloqueado} />

        {!chatBloqueado && (
        <View style={styles.inputContainer}>
          <TouchableOpacity style={styles.attachBtn} onPress={handleQuickReplyMenu}>
            <Ionicons name="add" size={24} color={Colors.textSecondary} />
          </TouchableOpacity>
          {/* El comprobante de la transferencia. Go Safely no toca ese dinero:
              esto solo hace que la foto llegue y quede. */}
          <TouchableOpacity
            style={styles.attachBtn}
            onPress={enviarComprobante}
            disabled={subiendoComprobante}
            accessibilityLabel="Enviar comprobante de transferencia"
          >
            {subiendoComprobante ? (
              <ActivityIndicator size="small" color={Colors.primary} />
            ) : (
              <Ionicons name="receipt-outline" size={22} color={Colors.textSecondary} />
            )}
          </TouchableOpacity>
          <TextInput 
            style={[styles.input, Platform.OS === 'web' && { outlineStyle: 'none', borderWidth: 0 } as any]}
            placeholder="Escribe un mensaje..."
            placeholderTextColor={Colors.textSecondary}
            value={inputText}
            onChangeText={setInputText}
            // Por debajo del tope de las reglas (2000, H20).
            maxLength={1000}
          />
          <TouchableOpacity style={[styles.sendBtn, { backgroundColor: inputText.trim() ? '#25D366' : Colors.textSecondary }]} onPress={sendMessage}>
            <Ionicons name="send" size={20} color="#FFF" style={{ marginLeft: 3 }} />
          </TouchableOpacity>
        </View>
        )}
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SIZES.padding,
    paddingTop: Platform.OS === 'ios' ? 10 : 40,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border,
  },
  backBtn: {
    padding: 5,
    marginRight: 10,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: Colors.text,
  },
  subtitle: {
    fontSize: 12,
    color: Colors.accent,
  },
  keyboardAvoid: {
    flex: 1,
  },
  chatContainer: {
    padding: SIZES.padding,
    paddingBottom: 20,
  },
  encryptionWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  encryptionText: {
    fontSize: 11,
    color: Colors.textSecondary,
    marginLeft: 5,
  },
  messageBubble: {
    maxWidth: '80%',
    padding: 15,
    borderRadius: 20,
    marginBottom: 15,
  },
  myBubble: {
    alignSelf: 'flex-end',
    backgroundColor: Colors.primary,
    borderBottomRightRadius: 5,
  },
  theirBubble: {
    alignSelf: 'flex-start',
    backgroundColor: Colors.card,
    borderBottomLeftRadius: 5,
    borderWidth: 1,
    borderColor: Colors.border,
  },
  messageText: {
    fontSize: 15,
    lineHeight: 22,
  },
  myText: {
    color: '#FFF',
  },
  theirText: {
    color: Colors.text,
  },
  messageTime: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.6)',
    alignSelf: 'flex-end',
    marginTop: 5,
  },
  actionContainer: {
    padding: 15,
    borderTopWidth: 1,
    borderTopColor: Colors.border,
    backgroundColor: Colors.background,
  },
  magicButton: {
    backgroundColor: Colors.accent,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 12,
  },
  magicButtonText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 16,
    marginLeft: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    paddingBottom: Platform.OS === 'web' ? 25 : 15,
    borderTopWidth: 1,
    borderTopColor: Colors.border,
    backgroundColor: Colors.background,
  },
  attachBtn: {
    marginRight: 10,
  },
  input: {
    flex: 1,
    height: 45,
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 22.5,
    paddingHorizontal: 15,
    fontSize: 15,
    color: Colors.text,
  },
  sendBtn: {
    width: 45,
    height: 45,
    borderRadius: 22.5,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 10,
  }
});
